/**
 * @career/definition-v2 compiler — thin adapter over the platform's ONE generic
 * compiler (`@platform/domain-framework`), injecting the shared career spine
 * (`careerPrimitives`) so `runtime.types.X.infer` resolves to the real inferred
 * shape (discriminated unions intact) end-to-end.
 */

import { compileDomain as frameworkCompileDomain } from "@platform/domain-framework/compiler"
import type { DomainDefinitionJSON } from "@platform/domain-framework/schema"
import { careerPrimitives } from "./career-primitives"

/**
 * Compile a career domain definition into the platform `DomainRuntime`,
 * injecting the shared career primitive spine.
 *
 * Generic over `const D` so a definition authored `as const satisfies
 * DomainDefinitionJSON` keeps its literal `types`, and the returned
 * `runtime.types.X.infer` resolves to the real inferred shape. The injected
 * `careerPrimitives` are threaded through as the compiler's `P`, so the merged
 * scope is `coreDomain & typeof careerPrimitives & D["types"]`.
 */
export function compileDomain<const D extends DomainDefinitionJSON>(def: D) {
  return frameworkCompileDomain(def, { primitives: careerPrimitives })
}

// URN / URL-template helpers — re-exported from the framework.
export { interpolate, parseUrn } from "@platform/domain-framework/compiler"

// Runtime types — the framework's superset `DomainRuntime<D, P>`.
export type {
  DomainRuntime,
  ResourceAccessor,
  ValidationResult,
} from "@platform/domain-framework/runtime"
